import { useId, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

/**
 * Hand-drawn style spot illustrations for empty states.
 *
 * Technique: line strokes run through a faint turbulence displacement (a hand-drawn line, not a
 * vector tool), and flat tints are offset from the line work like a two-colour riso print.
 * A3 palette: deep-blue line work, sea-teal laurel leaves, saffron accents, blue and teal tints.
 * All colours come from tokens, so they work in light and dark mode and on the blue field
 * (inside `surface-field` the tokens resolve to their field variants).
 */

export type IllustrationName =
  'contacts' | 'messages' | 'reviews' | 'chart' | 'inbox' | 'search' | 'sprout' | 'lock';

const INK = 'color-mix(in oklab, var(--gh-primary-ink) 72%, var(--gh-ink))';
const LEAF = 'var(--gh-area-messages)';
const SAFFRON = 'var(--gh-accent)';
const T_ACCENT = 'color-mix(in oklab, var(--gh-accent) 38%, transparent)';
const T_PRIMARY = 'color-mix(in oklab, var(--gh-primary-ink) 20%, transparent)';
const T_LEAF = 'color-mix(in oklab, var(--gh-area-messages) 26%, transparent)';
const T_INFO = 'color-mix(in oklab, var(--gh-area-messages) 22%, transparent)';
const T_NEG = 'color-mix(in oklab, var(--gh-negative) 24%, transparent)';

function Frame({
  children,
  className,
  title,
}: {
  children: (filter: string) => ReactNode;
  className?: string;
  title?: string;
}) {
  const id = useId().replace(/:/g, '');
  const f = `gh-wobble-${id}`;
  return (
    <svg
      viewBox="0 0 160 120"
      className={cn('h-auto w-40', className)}
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <defs>
        <filter id={f} x="-5%" y="-5%" width="110%" height="110%">
          <feTurbulence type="fractalNoise" baseFrequency="0.035" numOctaves="2" seed="7" />
          <feDisplacementMap in="SourceGraphic" scale="2.2" />
        </filter>
      </defs>
      {children(`url(#${f})`)}
    </svg>
  );
}

const stroke = { stroke: INK, strokeWidth: 1.7 } as const;
const fine = { stroke: INK, strokeWidth: 1.2 } as const;

function Ground() {
  return (
    <path
      d="M22 104c22-2.5 46-3 70-2.2 15 .5 31 1.3 46 .6"
      stroke="var(--gh-hairline-strong)"
      strokeWidth={1.4}
    />
  );
}

function Contacts({ f }: { f: string }) {
  return (
    <>
      <path
        d="M44 30c18-3 47-4 72-1.5 3 .3 4.5 2 4.6 4.8l1.3 50c.1 3-1.4 4.6-4.3 4.8-24 1.8-50 2-71 .5-3-.2-4.4-1.8-4.5-4.7L41 35c0-2.8 1-4.6 3-5z"
        fill={T_PRIMARY}
        transform="translate(5 -6)"
      />
      <g filter={f}>
        <Ground />
        <path d="M48 26c18-2 44-2.6 66-.8" {...fine} />
        <path
          d="M36 38c22-2.6 52-3.4 80-1.2 2.6.2 4 1.8 4 4.4l.6 46c0 2.7-1.4 4.2-4 4.4-26 2-53 2.2-78 .4-2.6-.2-4-1.7-4-4.3L34 42.4c0-2.6.7-4.1 2-4.4z"
          fill="var(--gh-surface)"
          {...stroke}
        />
        <circle cx="58" cy="58" r="8.5" fill={T_ACCENT} {...stroke} />
        <path d="M44.5 80.5c2.6-8 8-11.6 13.6-11.6s11 3.6 13.4 11.4" {...stroke} />
        <path d="M80 54.5h28M80 63h21M80 71.5h25" {...fine} />
        <path
          d="M111 31.5c1.8-4.4 5-7.4 9.6-8.6-.4 4.8-3.4 8.2-9.6 8.6z"
          fill={LEAF}
          stroke={LEAF}
          strokeWidth={1}
        />
        <path d="M111 31.5c3-1 6-3.2 8.2-6.4" stroke="var(--gh-surface)" strokeWidth={0.8} />
      </g>
    </>
  );
}

function Messages({ f }: { f: string }) {
  return (
    <>
      <rect
        x="34"
        y="50"
        width="70"
        height="46"
        rx="4"
        fill={T_ACCENT}
        transform="translate(5 4)"
      />
      <path
        d="M98 24c12.5-1 25-.6 31 .4 3 .5 4.4 2.2 4.4 5v17.6c0 2.8-1.6 4.4-4.4 4.6l-15 .6-7.8 7.4-.4-7.4-8-.2c-3-.1-4.6-1.7-4.6-4.6V29c0-2.8 1.6-4.6 4.8-5z"
        fill={T_PRIMARY}
        transform="translate(4 3)"
      />
      <g filter={f}>
        <Ground />
        <path
          d="M32 50.5c22-1.4 46-1.6 70-.4 2.4.1 3.6 1.4 3.6 3.8l.4 38c0 2.4-1.3 3.7-3.7 3.8-23 1-47 1-70 0-2.4-.1-3.7-1.4-3.6-3.8l.2-37.6c0-2.3 1.1-3.6 3.1-3.8z"
          fill="var(--gh-surface)"
          {...stroke}
        />
        <path d="M30.5 53c11 9.6 23 18 37 19.6 13-1.8 25-10 37.6-19.8" {...stroke} />
        <path d="M30.6 93c10-8.6 20-15 29.6-19.4M105.4 93.4C96 85 86 78.4 76 73.8" {...fine} />
        <path
          d="M96 22c12.5-1.2 25-.8 31.4.3 2.8.5 4.2 2.2 4.2 5v17.4c0 2.8-1.5 4.3-4.3 4.5l-15.3.6-8 7.6-.5-7.6-7.8-.2c-2.8-.1-4.2-1.7-4.2-4.5V27c0-2.8 1.4-4.6 4.5-5z"
          fill="var(--gh-surface)"
          {...stroke}
        />
        <circle cx="104" cy="36" r="2" fill={INK} />
        <circle cx="113" cy="36" r="2" fill={INK} />
        <circle cx="122" cy="36" r="2" fill={SAFFRON} stroke="none" />
        <path d="M18 62c4-.4 7.6-.4 10 0M14 72c5.4-.6 10-.5 14 0" {...fine} />
      </g>
    </>
  );
}

function Reviews({ f }: { f: string }) {
  const star =
    'M80 20.5l9.2 18.4 20.2 2.6-14.8 14 3.8 20-18.4-9.8-18.4 9.8 3.8-20-14.8-14 20.2-2.6z';
  return (
    <>
      <path d={star} fill={SAFFRON} transform="translate(4 3)" opacity={0.95} />
      <g filter={f}>
        <Ground />
        <path d={star} {...stroke} />
        {/* left laurel */}
        <path d="M58 98C44 90 37 78 37 62" {...stroke} />
        {[
          'M40.5 88c-6-.6-10-4-11.6-9.6 6 .2 10 3.6 11.6 9.6z',
          'M37.6 77.4c-5.6-1.8-8.8-6-9-11.8 5.6 1.4 8.8 5.6 9 11.8z',
          'M37 66c-4.8-3-7-7.6-6-13.2 4.8 2.8 7 7.4 6 13.2z',
          'M47 93c-1.4 5.8-5.4 9.2-11.4 9.8 1.2-6 5.2-9.4 11.4-9.8z',
          'M42.6 84.6c.6-6 4-10 9.6-11.4 0 6-3.4 10-9.6 11.4z',
          'M38.6 72.6c1.8-5.6 6-8.8 11.8-9-1.4 5.6-5.6 8.8-11.8 9z',
        ].map((d, i) => (
          <path key={i} d={d} fill={i % 2 ? T_LEAF : LEAF} stroke={LEAF} strokeWidth={1.1} />
        ))}
        {/* right laurel */}
        <path d="M102 98c14-8 21-20 21-36" {...stroke} />
        {[
          'M119.5 88c6-.6 10-4 11.6-9.6-6 .2-10 3.6-11.6 9.6z',
          'M122.4 77.4c5.6-1.8 8.8-6 9-11.8-5.6 1.4-8.8 5.6-9 11.8z',
          'M123 66c4.8-3 7-7.6 6-13.2-4.8 2.8-7 7.4-6 13.2z',
          'M113 93c1.4 5.8 5.4 9.2 11.4 9.8-1.2-6-5.2-9.4-11.4-9.8z',
          'M117.4 84.6c-.6-6-4-10-9.6-11.4 0 6 3.4 10 9.6 11.4z',
          'M121.4 72.6c-1.8-5.6-6-8.8-11.8-9 1.4 5.6 5.6 8.8 11.8 9z',
        ].map((d, i) => (
          <path key={i} d={d} fill={i % 2 ? T_LEAF : LEAF} stroke={LEAF} strokeWidth={1.1} />
        ))}
        <path d="M70 92c6.4.6 13.6.6 20 0" {...fine} />
      </g>
    </>
  );
}

function Chart({ f }: { f: string }) {
  return (
    <>
      <rect
        x="86"
        y="44"
        width="16"
        height="52"
        rx="2"
        fill={T_PRIMARY}
        transform="translate(4 -3)"
      />
      <g filter={f}>
        <Ground />
        <path d="M34 22c-.6 25-.6 51 .4 78 25 .6 63 .8 94-.4" {...stroke} />
        <path d="M30 34h4M30 52h4M30 70h4" {...fine} />
        <path
          d="M46 99c-.3-10-.2-19 .3-28 4-.3 8-.3 12 .1.4 9 .5 18 .2 27.8"
          fill="var(--gh-surface)"
          {...stroke}
        />
        <path
          d="M66 99c-.3-14-.2-26 .4-38 4-.3 8-.3 12.2.1.4 12 .4 25 0 37.8"
          fill={T_ACCENT}
          {...stroke}
        />
        <path
          d="M86 99c-.4-18-.3-35 .3-52.4 4-.3 8.3-.3 12.4.1.5 17 .5 34.4 0 52"
          fill="var(--gh-surface)"
          {...stroke}
        />
        <path
          d="M106 99c-.3-21-.3-42 .4-63 4-.4 8.2-.3 12.2.2.4 21 .4 41.6 0 62.6"
          fill="var(--gh-surface)"
          {...stroke}
        />
        <path
          d="M44 60c12-6 20-12 30-14 9.6-2 16-10 24-18 6-6 12-8 18-9"
          stroke={LEAF}
          strokeWidth={2}
        />
        <path d="M110 15.6l7 3.6-3.4 7" stroke={LEAF} strokeWidth={2} />
        <circle cx="74" cy="46" r="2.6" fill={SAFFRON} stroke="none" />
      </g>
    </>
  );
}

function Inbox({ f }: { f: string }) {
  return (
    <>
      <path
        d="M34 66h28c2 6 8 10 18 10s16-4 18-10h28l-4 26c-.4 3-2.4 4.6-5.4 4.6H43.4c-3 0-5-1.6-5.4-4.6z"
        fill={T_INFO}
        transform="translate(4 3)"
      />
      <g filter={f}>
        <Ground />
        <path d="M58 24c14-1 30-1 44 .2l.8 34" {...fine} />
        <path d="M58 24l-.6 34" {...fine} />
        <path d="M62 34h30M62 42h22" {...fine} />
        <path
          d="M44 46c24-1 48-1.2 72 0l14 20-4.2 27c-.5 3-2.5 4.6-5.5 4.6H41.4c-3 0-5-1.6-5.4-4.6L31 66z"
          fill="var(--gh-surface)"
          {...stroke}
        />
        <path d="M31.4 66.4h28.8c2 6.4 8.4 10.6 19.8 10.6S97.6 72.8 99.6 66.4h30" {...stroke} />
        <path d="M44 46c-4 6.6-8.4 13.4-12.8 20.2M116 46c4.4 6.6 9 13.4 14 20.2" {...fine} />
        <path d="M74 88c4 .4 8 .4 12 0" {...fine} />
      </g>
    </>
  );
}

function Search({ f }: { f: string }) {
  return (
    <>
      <circle cx="92" cy="52" r="20" fill={T_ACCENT} transform="translate(4 3)" />
      <g filter={f}>
        <Ground />
        <path
          d="M38 22c16-1 33-1.2 48 0 2.4.2 3.6 1.6 3.6 4l.8 66c0 2.4-1.2 3.8-3.6 4-16 1-32 1-47.6 0-2.4-.2-3.6-1.6-3.6-4L34.6 26c0-2.4 1.2-3.8 3.4-4z"
          fill="var(--gh-surface)"
          {...stroke}
        />
        <path d="M44 36h30M44 45h22M44 54h26M44 63h16M44 72h24" {...fine} />
        <circle
          cx="92"
          cy="52"
          r="19"
          fill="var(--gh-surface)"
          fillOpacity={0.6}
          {...stroke}
          strokeWidth={2.2}
        />
        <path d="M106 66l17 17.6" stroke={INK} strokeWidth={5} />
        <path d="M86 46c2-3 5-4.4 8.6-4" stroke="var(--gh-ink-subtle)" strokeWidth={1.4} />
      </g>
    </>
  );
}

function Sprout({ f }: { f: string }) {
  return (
    <>
      <path
        d="M58 74h44l-5 24c-.6 2.4-2 3.6-4.6 3.6H67.6c-2.6 0-4-1.2-4.6-3.6z"
        fill={T_NEG}
        transform="translate(4 2)"
      />
      <g filter={f}>
        <Ground />
        <path
          d="M56 72c16-.8 32-.8 48 0l-.4 7.6c-16 .8-31.4.8-47.2 0z"
          fill="var(--gh-surface)"
          {...stroke}
        />
        <path
          d="M58.6 79.6l4.4 19.6c.6 2.4 2 3.6 4.6 3.6h24.8c2.6 0 4-1.2 4.6-3.6l4.2-19.6"
          fill="var(--gh-surface)"
          {...stroke}
        />
        <path d="M80 72c-.6-12 .4-24 4-34" stroke={LEAF} strokeWidth={2} />
        <path
          d="M82 52c-9-1-15.6-6.6-18-15.6 9.6.6 16 6 18 15.6z"
          fill={LEAF}
          stroke={LEAF}
          strokeWidth={1.2}
        />
        <path
          d="M83.6 43c2-9 8.4-14.6 17.6-15.6-1.6 9.4-8 14.8-17.6 15.6z"
          fill={T_LEAF}
          stroke={LEAF}
          strokeWidth={1.2}
        />
        <path
          d="M85.6 33.4l1.6 3.4 3.6.5-2.6 2.5.6 3.6-3.2-1.7-3.2 1.7.6-3.6-2.6-2.5 3.6-.5z"
          fill={SAFFRON}
          stroke="none"
          transform="translate(16 -16)"
        />
        {/* scaffold / ruler */}
        <path d="M118 102l10-62M126 102l10-62M120 90h10M122 78h10M124 66h10M126 54h10" {...fine} />
        <path d="M32 98l14-30 4 2-13 30z" fill={T_ACCENT} {...fine} />
        <path d="M46 68l2-6 2 8" {...fine} />
      </g>
    </>
  );
}

function Lock({ f }: { f: string }) {
  return (
    <>
      <rect
        x="54"
        y="52"
        width="52"
        height="42"
        rx="6"
        fill={SAFFRON}
        opacity={0.38}
        transform="translate(5 4)"
      />
      <g filter={f}>
        <Ground />
        <path d="M64 52c-.6-14 6-24 16-24s16.6 10 16 24" {...stroke} strokeWidth={2.2} />
        <path
          d="M56 52c16-1 32-1 48 0 2.4.2 3.6 1.6 3.6 4l.4 34c0 2.4-1.2 3.8-3.6 4-16 1-32 1-48.4 0-2.4-.2-3.6-1.6-3.6-4l.2-34c0-2.4 1.2-3.8 3.4-4z"
          fill="var(--gh-surface)"
          {...stroke}
        />
        <path
          d="M80 66.5l2.6 5.2 5.8.8-4.2 4 1 5.8L80 79.6l-5.2 2.7 1-5.8-4.2-4 5.8-.8z"
          fill={SAFFRON}
          stroke={INK}
          strokeWidth={1.1}
        />
        <path
          d="M118 40c3.6-6 9-9 15.6-9.4-1.4 6.8-6.6 10-15.6 9.4zM120 52c6-2.4 11.6-1.4 16 3-6 2.2-11.4 1.2-16-3z"
          fill={LEAF}
          stroke={LEAF}
          strokeWidth={1}
        />
        <path d="M112 62c4-8 6-16 6-26" stroke={LEAF} strokeWidth={1.6} />
      </g>
    </>
  );
}

const MAP: Record<IllustrationName, (p: { f: string }) => ReactNode> = {
  contacts: Contacts,
  messages: Messages,
  reviews: Reviews,
  chart: Chart,
  inbox: Inbox,
  search: Search,
  sprout: Sprout,
  lock: Lock,
};

export function Illustration({
  name,
  className,
  title,
}: {
  name: IllustrationName;
  className?: string;
  title?: string;
}) {
  const C = MAP[name];
  return (
    <Frame className={className} title={title}>
      {(f) => <C f={f} />}
    </Frame>
  );
}

export const ILLUSTRATIONS = Object.keys(MAP) as IllustrationName[];
